import React from "react";
import type { TypographyProps } from "@mui/joy";
import { Typography } from "@mui/joy";
import { textTokens } from "../../styles/globalStyles";

const TITLE_LEVELS = new Set(["h1", "h2", "h3", "h4", "title-lg", "title-md", "title-sm"]);

const COLOR_MAP = {
  /** Corps sur fond sombre (blanc) */
  primary: textTokens.color.bodyOnDark,
  secondary: textTokens.color.secondary,
  muted: textTokens.color.muted,
  /** Titres / sections (jaune) — remplace l’ancien bleu « accent » pour le texte */
  accent: textTokens.color.title,
  title: textTokens.color.title,
  legend: textTokens.color.legend,
  /** Corps sur fond blanc / feuille claire */
  onLight: textTokens.color.bodyOnLight,
  onLightSecondary: textTokens.color.onLightSecondary,
  onLightMuted: textTokens.color.onLightMuted,
} as const;

const RESPONSIVE_FONT: Record<string, { xs: string; sm: string }> = {
  h1: { xs: "1.5rem", sm: "2rem" },
  h2: { xs: "1.25rem", sm: "1.5rem" },
  h3: { xs: "1.1rem", sm: "1.25rem" },
  h4: { xs: "0.95rem", sm: "1rem" },
  "title-lg": { xs: "0.95rem", sm: "1.125rem" },
  "title-md": { xs: "0.875rem", sm: "1rem" },
  "title-sm": { xs: "0.8rem", sm: "0.875rem" },
  "body-lg": { xs: "0.875rem", sm: "1rem" },
  "body-md": { xs: "0.8rem", sm: "0.875rem" },
  "body-sm": { xs: "0.75rem", sm: "0.8rem" },
  "body-xs": { xs: "0.7rem", sm: "0.75rem" },
};

export type DarkTypographyColorVariant = keyof typeof COLOR_MAP;

interface DarkTypographyProps extends TypographyProps {
  children: React.ReactNode;
  colorVariant?: DarkTypographyColorVariant;
}

const DarkTypography = React.forwardRef<HTMLElement, DarkTypographyProps>(function DarkTypography(
  { children, colorVariant: colorVariantProp, level, sx = {}, ...props },
  ref,
) {
  const resolvedVariant: DarkTypographyColorVariant =
    colorVariantProp !== undefined
      ? colorVariantProp
      : level && TITLE_LEVELS.has(level)
        ? "title"
        : "primary";

  const responsiveFs = level ? RESPONSIVE_FONT[level] : undefined;

  return (
    <Typography
      ref={ref}
      {...props}
      level={level}
      sx={{
        color: COLOR_MAP[resolvedVariant],
        ...(responsiveFs ? { fontSize: responsiveFs } : {}),
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
});

export default DarkTypography;
